import React, { useCallback, useMemo, useState, useEffect, useRef } from "react";
import PageHeader from "Components/PageHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flex, InputNumber, Typography } from "antd";
import { ArrowLeftOutlined, FileExcelOutlined, LoadingOutlined } from "@ant-design/icons";
import { SbTable, SbTag, SbButton, SbTabs, SbSpin, SbNotification, SbEmpty, SbModal } from "uiCore";
import { getPaicRedesignPlanDetails } from "Api/apiService";
import { PageHeaderStyled, HeaderLeftStyled, HeaderRightStyled, HeadingRowStyled, PageBodyStyled } from "styles/common.styles";
import { ClickableTableWrapperStyled } from "./sharedTable.styles";
import { PageTransitionStyled } from "./talentMobility.animations";
import { BackLinkStyled } from "./RoleDetailV2.styles";
import { usePublishedOrgRedesignVersions } from "Pages/OrgDesign/useOrgDesignData";
import { buildVersionColumns, formatVersionRowLabel } from "Pages/OrgDesign/orgDesignVersionColumns";
import { activatableRowProps } from "Pages/OrgDesign/orgDesign.constants";
import OrgDesignSummaryBridge from "Pages/OrgDesign/components/OrgDesignSummaryBridge";
import usePaicExecutionStore from "Store/usePaicExecutionStore";
import usePaicRedesignPlanActions from "./usePaicRedesignPlanActions";
import usePaicVersionSwitchGuard, { needsSwitchConfirm } from "./usePaicVersionSwitchGuard";
import PlanSaveButton from "./components/PlanSaveButton";
import usePaicRedesignPlanStore, {
  getOtherDraftVersion,
  usePaicVersionDraftRoles,
} from "Store/usePaicRedesignPlanStore";
import {
  PAIC_VERSIONS_PATH,
  ROLES_ADDED_API_STATUS,
  ROLES_REDUCED_API_STATUS,
  buildPaicVersionBaseline,
  filterRolesByApiStatus,
  pickPlanOrganization,
} from "./PaicOrgRedesignV2.utils";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

// Simulation result counts — shared by the cells and their column filters.
const getReducedCount = (r) => (r.statusCounts?.REDUCED || 0) + (r.statusCounts?.REDUNDANT || 0);
const getAugmentedCount = (r) => r.statusCounts?.["AI AUGMENTED"] || 0;
const getAddedCount = (r) => r.statusCounts?.CREATED || 0;

// "After HC (Approved)" is read-only for now in both Roles Reduced and Roles
// Added — the plan keeps each role's AI-proposed headcount. Set to true to
// make the inputs editable again (edits are saved to the plan as before).
const IS_AFTER_HC_EDITABLE = false;

// Centers a header's title + filter icon over its align: "center" values
// (ClickableTableWrapperStyled's tm-col-header-center rule).
const centeredHeaderCell = () => ({ className: "tm-col-header-center" });

// Roles Reduced / Roles Added tabs (the version's REDUCED / INCREASED roles)
// — same table, same flow. Clicking a role name navigates to Org Redesign's
// OWN reduction route (/talent-mobility/paic-org-redesign/reduction/:roleId,
// RouteConfigV2.js), a separate entry point onto the exact same
// RedeploymentReductionDetailV2 page Redeployment Center's own Role View
// uses (occupants ranked by SMI, cutoff at the target headcount, mark
// below-cutoff people available) — see RedeploymentCenterV2's
// handleRoleRowClick for the near-identical URL shape. That route renders it
// through PaicReductionDetailV2, so "Back" returns here and its Save buttons
// save this PAIC plan instead of a redeployment plan.
// record.sb_unique_id is a real job_desc id (same id space Redeployment
// Center's jd_sb_unique_id lives in), so no separate "linked TM role" lookup
// is needed here — that ORG_DESIGN_TM_ROLE_ALIASES indirection was only ever
// for this tab's own (now-removed) inline occupant panel. An INCREASED
// role's target is above its headcount, so nobody shows as At Risk there;
// its picks save under the plan's roles_increased.
//
// "After HC (Approved)" edits and the employees picked in Reduction Detail
// live in the PAIC plan draft (draftRoles, keyed by sb_unique_id), so they
// survive tab switches and the round trip through Reduction Detail. Opening
// a role adds the version to the draft first — PaicReductionDetailV2 only
// accepts roles it can save picks into, and versionId/roleKey tell it which.
const PlannableRolesTab = ({
  roles,
  emptyText,
  design,
  versionId,
  draftRoles,
  onTargetChange,
  onBeforeOpenRole,
  showShortlisted,
}) => {
  const navigate = useNavigate();
  const getAfterHc = (r) =>
    draftRoles?.[r.sb_unique_id]?.target_position ?? design[r.sb_unique_id]?.after_hc ?? r.total_positions;
  // Employees the plan shortlisted for the role (a saved plan's, after Edit,
  // plus any picks since); 0 for a role not in the draft yet.
  const getShortlistedCount = (r) => draftRoles?.[r.sb_unique_id]?.available_employee_targets?.length || 0;

  const openReductionDetail = (r) => {
    onBeforeOpenRole();
    const params = new URLSearchParams({
      target: getAfterHc(r),
      current: r.total_positions ?? "",
      title: r.job_title ?? "",
      versionId,
      roleKey: r.sb_unique_id,
    });
    navigate(`/talent-mobility/paic-org-redesign/reduction/${r.jd_sb_unique_id ?? r.sb_unique_id}?${params}`);
  };

  return (
    <ClickableTableWrapperStyled>
      <SbTable
        columns={[
          {
            title: "Role",
            dataIndex: "job_title",
            render: (title, r) => (
              // No href, so it needs a role/tabIndex/Enter-Space handler to be
              // keyboard reachable — the same helper the versions table rows use.
              <Typography.Link
                strong
                onClick={() => openReductionDetail(r)}
                {...activatableRowProps(() => openReductionDetail(r))}
              >
                {title}
              </Typography.Link>
            ),
          },
          { title: "Before HC", dataIndex: "total_positions", align: "center", onHeaderCell: centeredHeaderCell },
          {
            title: "After HC (Approved)",
            key: "afterHc",
            align: "center",
            onHeaderCell: centeredHeaderCell,
            filterValue: getAfterHc,
            render: (_, r) => (
              <InputNumber
                size="small"
                min={0}
                value={getAfterHc(r)}
                disabled={!IS_AFTER_HC_EDITABLE}
                onClick={(event) => event.stopPropagation()}
                onChange={(value) => onTargetChange(r.sb_unique_id, value)}
              />
            ),
          },
          // Only when editing a saved plan (View Planning's Edit).
          ...(showShortlisted
            ? [
                {
                  title: "Shortlisted",
                  key: "shortlisted",
                  align: "center",
                  onHeaderCell: centeredHeaderCell,
                  filterValue: getShortlistedCount,
                  render: (_, r) => getShortlistedCount(r),
                },
              ]
            : []),
        ]}
        dataSource={roles}
        rowKey="sb_unique_id"
        pagination={false}
        locale={{ emptyText }}
      />
    </ClickableTableWrapperStyled>
  );
};

// Labels of versions opened this session, for the unsaved-changes dialog on
// a version page (which doesn't load the versions list). Labels only.
const versionLabelCache = new Map();

const ANALYTICS_FROM = "talent_mobility_paic_org_redesign";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.PAIC_ORG_REDESIGN_VIEWED;

const PaicOrgRedesignV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate = useNavigate();
  const location = useLocation();
  // Present only on /talent-mobility/paic-org-redesign/:versionId — absent
  // on the bare landing path.
  const { versionId } = useParams();

  // Arriving from View Planning's Edit action — it navigates right away to the
  // plan's version page (see ViewPlanningV2's handleEditPaicPlan) carrying
  // which plan to load as router state; same handoff as RedeploymentCenterV2's
  // loadPlanId effect. Fetch that plan and load its one organization
  // (pickPlanOrganization) into the PAIC draft as its saved state, so Update
  // Plan saves back to it. The version page waits for it (spinner) so nothing
  // is edited before the saved shortlists are in. A plan with no visible
  // organization isn't loaded at all — Update would otherwise attach whatever
  // version is opened next to it.
  // The ref guards against firing twice. The router state is dropped up front
  // (so a re-render or Back can't re-run the load), and a response that lands
  // after the user has left this page is ignored — loading it would re-fill a
  // draft LayoutV2 has just reset.
  const [isLoadingPlan, setIsLoadingPlan] = useState(Boolean(location.state?.loadPlanId));
  const hasLoadedPlanRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    const loadPlanId = location.state?.loadPlanId;
    if (!loadPlanId || hasLoadedPlanRef.current) return;
    hasLoadedPlanRef.current = true;
    const loadPlanName = location.state?.loadPlanName || "";
    navigate(location.pathname, { replace: true });
    (async () => {
      try {
        const response = await getPaicRedesignPlanDetails(loadPlanId);
        if (!isMountedRef.current) return;
        const organization = pickPlanOrganization(response?.organizations, versionId);
        if (!organization) {
          SbNotification.info("Unable to open this plan", "This plan's organization is no longer available.");
          return;
        }
        usePaicRedesignPlanStore
          .getState()
          .loadPlan({ planId: loadPlanId, planName: loadPlanName, organizations: [organization] });
        const planVersionPath = `${PAIC_VERSIONS_PATH}/${organization.sb_org_redesign_unique_id}`;
        if (planVersionPath !== location.pathname) navigate(planVersionPath, { replace: true });
      } catch (error) {
        if (!isMountedRef.current) return;
        SbNotification.error(
          "Unable to load PAIC redesign plan",
          error?.response?.data?.message || "Please try again."
        );
      } finally {
        if (isMountedRef.current) setIsLoadingPlan(false);
      }
    })();
  }, [location.state, location.pathname, navigate, versionId]);
  // The versions list is only needed on the list page — a version page
  // fetches just its own version (fetchVersionDraft), so coming back from
  // Reduction Detail doesn't re-download the whole list.
  const { versions, loading, opening, fetchVersionDraft } = usePublishedOrgRedesignVersions({
    skipList: Boolean(versionId),
  });
  const results = usePaicExecutionStore((state) => state.results);
  // Same published GET /versions rows as Org Design's "Previous Iterations";
  // the full roles/design snapshot is only fetched once a row is opened.
  const [selectedVersion, setSelectedVersion] = useState(null);
  const snapshot = selectedVersion?.snapshot;
  // Only the snapshot of the version in the URL counts — selectedVersion can
  // briefly still hold the previously opened one.
  const versionSnapshot = selectedVersion?.versionId === versionId ? snapshot : null;

  // PAIC plan draft (usePaicRedesignPlanStore) for this version. The version
  // joins the draft on its first edit or role open, seeded from
  // baselineRoles; until then the tabs show the snapshot's own values. The
  // Save buttons stay disabled until the draft actually differs from that
  // baseline or the last save.
  const roleTabs = useMemo(() => {
    if (!versionSnapshot) return null;
    const { roles, apiRoleStatuses } = versionSnapshot;
    return {
      reducedRoles: filterRolesByApiStatus(roles, apiRoleStatuses, ROLES_REDUCED_API_STATUS),
      increasedRoles: filterRolesByApiStatus(roles, apiRoleStatuses, ROLES_ADDED_API_STATUS),
    };
  }, [versionSnapshot]);
  const baselineRoles = useMemo(
    () =>
      roleTabs
        ? buildPaicVersionBaseline({
            plannableRoles: [...roleTabs.reducedRoles, ...roleTabs.increasedRoles],
            design: versionSnapshot.design,
            apiRoleStatuses: versionSnapshot.apiRoleStatuses,
          })
        : [],
    [roleTabs, versionSnapshot]
  );
  const draftRoles = usePaicVersionDraftRoles(versionId);
  const isEditingSavedPlan = usePaicRedesignPlanStore((state) => state.isEditingSavedPlan);
  const ensureVersion = usePaicRedesignPlanStore((state) => state.ensureVersion);
  const updateRole = usePaicRedesignPlanStore((state) => state.updateRole);
  const ensureVersionInDraft = useCallback(
    () => ensureVersion(versionId, baselineRoles),
    [ensureVersion, versionId, baselineRoles]
  );
  const handleTargetChange = useCallback(
    (roleKey, targetPosition) => {
      ensureVersionInDraft();
      updateRole(versionId, roleKey, { target_position: targetPosition });
    },
    [ensureVersionInDraft, updateRole, versionId]
  );
  const { hasDraft, isSubmitting, planName, planLabel, isUpdate, savePlan } = usePaicRedesignPlanActions({
    onClosed: () => navigate(PAIC_VERSIONS_PATH),
  });

  // The URL (versionId) is the single source of truth for which version is
  // open — selectedVersion is just its fetched cache. This effect is what
  // makes "Back" from the Reduction Detail page (or a reload/shared link)
  // actually restore the right version, instead of only ever landing on the
  // versions list the way a plain `navigate("/talent-mobility/paic-org-redesign")`
  // would if the open version lived in local state alone.
  //
  // Guarded by a ref (claimed synchronously, before any await), NOT by
  // `opening`/`selectedVersion` directly: fetchVersionDraft flips `opening`
  // true then false via its own setState calls, and that false lands in a
  // render that fires BEFORE this effect's `setSelectedVersion` call below
  // has committed — so a guard reading `opening`/`selectedVersion` sees a
  // window where neither has caught up yet, re-passes, and calls
  // fetchVersionDraft again, forever. The ref has no such window: it's set
  // the instant this effect decides to fetch, so no re-render in between can
  // ever see it as "not yet claimed". The claim is released when the fetch
  // is abandoned (navigated away mid-fetch) or fails, so opening the same
  // version again — or Retry — fetches it again instead of spinning forever.
  // An unavailable version (not found, no access, unpublished, archived) is
  // final for this visit — it shows "isn't available" without retrying.
  const fetchedVersionIdRef = useRef(null);
  const [failedVersionId, setFailedVersionId] = useState(null);
  const [unavailableVersionId, setUnavailableVersionId] = useState(null);
  useEffect(() => {
    if (!versionId || fetchedVersionIdRef.current === versionId) return undefined;
    if (failedVersionId === versionId || unavailableVersionId === versionId) return undefined;
    fetchedVersionIdRef.current = versionId;
    let cancelled = false;
    let settled = false;
    (async () => {
      const result = await fetchVersionDraft(versionId);
      settled = true;
      if (cancelled) return;
      if (result.status !== "ok") {
        fetchedVersionIdRef.current = null;
        if (result.status === "unavailable") setUnavailableVersionId(versionId);
        else setFailedVersionId(versionId);
        return;
      }
      const label = formatVersionRowLabel(result.record);
      versionLabelCache.set(versionId, label);
      setSelectedVersion({ versionId, label, snapshot: result.draft });
    })();
    return () => {
      cancelled = true;
      if (!settled && fetchedVersionIdRef.current === versionId) fetchedVersionIdRef.current = null;
    };
  }, [versionId, failedVersionId, unavailableVersionId, fetchVersionDraft]);

  // One plan covers one version: opening a different version starts a new
  // plan. A draft of another version that was only browsed is dropped without
  // asking (the version page's effect below). One with unsaved changes, or
  // the saved plan being edited (opened via View Planning's Edit), asks first
  // — keep editing it, or leave it and start a plan for the clicked version.
  // The list asks on row click, before navigating; the version page asks too,
  // for a pasted link or Back/Forward — once the version has loaded
  // (usePaicVersionSwitchGuard), so an unavailable one never asks.
  // pendingSwitch: { fromVersionId, toVersionId, hasUnsavedChanges }
  // (toVersionId null when already on the target version's page).
  const [pendingSwitch, setPendingSwitch] = useState(null);

  const handleOpenVersion = (record) => {
    if (opening) return;
    const targetVersionId = record.sb_org_redesign_unique_id;
    const otherVersion = getOtherDraftVersion(usePaicRedesignPlanStore.getState(), targetVersionId);
    if (needsSwitchConfirm(otherVersion)) {
      setPendingSwitch({
        fromVersionId: otherVersion.versionId,
        toVersionId: targetVersionId,
        hasUnsavedChanges: otherVersion.hasUnsavedChanges,
      });
      return;
    }
    navigate(`${PAIC_VERSIONS_PATH}/${targetVersionId}`);
  };

  usePaicVersionSwitchGuard({
    versionId,
    isVersionReady: Boolean(versionSnapshot) && !isLoadingPlan,
    onAsk: (otherVersion) =>
      setPendingSwitch({
        fromVersionId: otherVersion.versionId,
        toVersionId: null,
        hasUnsavedChanges: otherVersion.hasUnsavedChanges,
      }),
  });

  const versionLabel = (id) => {
    const record = versions.find((v) => v.sb_org_redesign_unique_id === id);
    return record ? formatVersionRowLabel(record) : versionLabelCache.get(id) ?? null;
  };
  const switchFromLabel = versionLabel(pendingSwitch?.fromVersionId) || "another organization";
  const switchToLabel = versionLabel(pendingSwitch?.toVersionId ?? versionId) || "this organization";
  // Back to the version being kept. From a version page (toVersionId null)
  // the history entry is replaced, so browser Back doesn't return to the page
  // that asked; from the list it's a normal step forward. Esc does the same —
  // the safe choice, nothing is discarded.
  const keepEditing = () => {
    const { fromVersionId, toVersionId } = pendingSwitch;
    setPendingSwitch(null);
    navigate(`${PAIC_VERSIONS_PATH}/${fromVersionId}`, { replace: toVersionId === null });
  };
  const switchPlanModal = (
    <SbModal
      open={Boolean(pendingSwitch)}
      title={pendingSwitch?.hasUnsavedChanges ? "Unsaved changes" : "Leave this plan?"}
      closable={false}
      maskClosable={false}
      onCancel={keepEditing}
      footer={[
        <SbButton key="keep" buttonType="secondary" onClick={keepEditing}>
          Keep editing
        </SbButton>,
        <SbButton
          key="discard"
          buttonType="primary"
          onClick={() => {
            const { toVersionId } = pendingSwitch;
            usePaicRedesignPlanStore.getState().reset();
            setPendingSwitch(null);
            if (toVersionId) navigate(`${PAIC_VERSIONS_PATH}/${toVersionId}`);
          }}
        >
          {pendingSwitch?.hasUnsavedChanges ? "Discard and start new plan" : "Start new plan"}
        </SbButton>,
      ]}
    >
      {pendingSwitch?.hasUnsavedChanges ? (
        <Text>
          You have unsaved changes for <Text strong>{switchFromLabel}</Text>. A plan covers one organization, so
          starting a plan for <Text strong>{switchToLabel}</Text> discards them.
        </Text>
      ) : (
        <Text>
          You&apos;re editing plan <Text strong>{planName}</Text> for <Text strong>{switchFromLabel}</Text>. Opening{" "}
          <Text strong>{switchToLabel}</Text> leaves this plan and starts a new one. The saved plan isn&apos;t changed.
        </Text>
      )}
    </SbModal>
  );

  // Version page's Export CSV: this version's plannable roles as the plan
  // has them — approved headcount and shortlist from the draft, falling back
  // to the AI proposal for roles not drafted yet.
  const handleExportCsv = () => {
    if (!roleTabs) return;
    const rows = [...roleTabs.reducedRoles, ...roleTabs.increasedRoles].map((r) => {
      const draftRole = draftRoles?.[r.sb_unique_id];
      return {
        Role: r.job_title,
        "Change Type": versionSnapshot.apiRoleStatuses?.[r.sb_unique_id] || "",
        "Before HC": r.total_positions,
        "After HC (Approved)":
          draftRole?.target_position ?? versionSnapshot.design[r.sb_unique_id]?.after_hc ?? r.total_positions,
        "Shortlisted Employees": draftRole?.available_employee_targets?.length || 0,
      };
    });
    exportRowsAsCsv(rows, `paic-org-redesign-${versionId}`);
  };

  if (!versionId) {
    return (
      <PageTransitionStyled>
        <PageHeader
          title="PAIC Org Redesign"
          // The plan being edited (View Planning's Edit). Opening another
          // version from here asks before leaving it.
          titleExtra={
            isLoadingPlan ? (
              <SbTag color="processing" icon={<LoadingOutlined spin />} style={{ fontSize: 14 }}>
                Plan name...
              </SbTag>
            ) : (
              isUpdate && (
                <SbTag color="blue" style={{ fontSize: 14 }}>
                  {planLabel}
                </SbTag>
              )
            )
          }
          subtitle={
            <>
              <Text type="secondary">
                Pick an already-approved Organisation Design version (each carries the PAIC group/version it was generated from) to
                begin redeployment/hiring execution against its approved headcount.
              </Text>
            </>
          }
          onBack={() => navigate("/talent-mobility")}
          backLabel="Back to Talent Mobility"
        />

        <ClickableTableWrapperStyled>
          <SbTable
            columns={buildVersionColumns(versions, { splitVersion: false })}
            dataSource={versions}
            rowKey="sb_org_redesign_unique_id"
            loading={loading || opening || isLoadingPlan}
            sticky
            pagination={{ defaultPageSize: 10, pageSizeOptions: ["10", "20", "30"], showSizeChanger: true }}
            onRow={(record) => ({ onClick: () => handleOpenVersion(record), ...activatableRowProps(() => handleOpenVersion(record)) })}
          />
        </ClickableTableWrapperStyled>

        {results.length > 0 && (
          <>
            <HeadingRowStyled style={{ padding: "16px 24px 0" }}>
              <Title level={5}>Previous Simulations</Title>
            </HeadingRowStyled>
            <ClickableTableWrapperStyled>
              <SbTable
                columns={[
                  { title: "Version", dataIndex: "versionLabel" },
                  { title: "Saved", dataIndex: "savedAt", render: (v) => new Date(v).toLocaleString() },
                  { title: "Reduced", key: "reduced", filterValue: getReducedCount, render: (_, r) => getReducedCount(r) },
                  { title: "Augmented", key: "augmented", filterValue: getAugmentedCount, render: (_, r) => getAugmentedCount(r) },
                  { title: "Added", key: "added", filterValue: getAddedCount, render: (_, r) => getAddedCount(r) },
                ]}
                dataSource={results}
                rowKey="resultId"
                pagination={false}
              />
            </ClickableTableWrapperStyled>
          </>
        )}
        {switchPlanModal}
      </PageTransitionStyled>
    );
  }

  // A version id is in the URL (deep link, reload, "Back" from Reduction
  // Detail, or View Planning's Edit) but the page isn't ready yet: unknown
  // id, a failed fetch (with Retry), the snapshot still loading, or the plan
  // being edited still loading.
  if (!snapshot || selectedVersion.versionId !== versionId || isLoadingPlan) {
    const isUnknownVersion = unavailableVersionId === versionId;
    const isFailed = failedVersionId === versionId;
    return (
      <PageTransitionStyled>
        <PageHeaderStyled>
          <HeaderLeftStyled>
            <BackLinkStyled onClick={() => navigate(PAIC_VERSIONS_PATH)}>
              <ArrowLeftOutlined /> Back to Versions
            </BackLinkStyled>
          </HeaderLeftStyled>
        </PageHeaderStyled>
        {isUnknownVersion || isFailed ? (
          <SbEmpty
            description={
              isUnknownVersion
                ? "This Organisation Design version isn't available."
                : "This Organisation Design version couldn't be loaded."
            }
          >
            {isFailed && !isUnknownVersion && (
              <SbButton buttonType="secondary" onClick={() => setFailedVersionId(null)}>
                Retry
              </SbButton>
            )}
          </SbEmpty>
        ) : (
          <Flex justify="center" style={{ padding: "48px 0" }}>
            <SbSpin size="large" />
          </Flex>
        )}
        {switchPlanModal}
      </PageTransitionStyled>
    );
  }

  const plannableRolesTabProps = {
    design: snapshot.design,
    versionId,
    draftRoles,
    onTargetChange: handleTargetChange,
    onBeforeOpenRole: ensureVersionInDraft,
    showShortlisted: isEditingSavedPlan,
  };

  return (
    <PageTransitionStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <BackLinkStyled onClick={() => navigate(PAIC_VERSIONS_PATH)}>
            <ArrowLeftOutlined /> Back to Versions
          </BackLinkStyled>
          <HeadingRowStyled>
            <Title level={4}>{selectedVersion.label}</Title>
            <SbTag color={isUpdate ? "blue" : "gold"} style={{ fontSize: 14 }}>{planLabel}</SbTag>
          </HeadingRowStyled>
        </HeaderLeftStyled>
        <HeaderRightStyled>
          <SbButton buttonType="secondary" icon={<FileExcelOutlined />} onClick={handleExportCsv}>
            Export CSV
          </SbButton>
          <PlanSaveButton
            planTypeLabel="PAIC Redesign Plan"
            hasDraft={hasDraft}
            isUpdate={isUpdate}
            planName={planName}
            isSubmitting={isSubmitting}
            onSave={savePlan}
          />
          <PlanSaveButton
            planTypeLabel="PAIC Redesign Plan"
            buttonType="secondary"
            closeAfter
            hasDraft={hasDraft}
            isUpdate={isUpdate}
            planName={planName}
            isSubmitting={isSubmitting}
            onSave={savePlan}
          />
        </HeaderRightStyled>
      </PageHeaderStyled>

      <OrgDesignSummaryBridge
        stats={snapshot.stats}
        statusCounts={snapshot.status_counts}
        roleCounts={snapshot.roleCounts}
        entryOption={snapshot.entryOption}
      />

      <PageBodyStyled>
        <SbTabs
          items={[
            {
              key: "reduced",
              label: "Roles Reduced",
              children: (
                <PlannableRolesTab
                  roles={roleTabs.reducedRoles}
                  emptyText="No reduced roles in this version."
                  {...plannableRolesTabProps}
                />
              ),
            },
            {
              key: "added",
              label: "Roles Added",
              children: (
                <PlannableRolesTab
                  roles={roleTabs.increasedRoles}
                  emptyText="No increased roles in this version."
                  {...plannableRolesTabProps}
                />
              ),
            },
          ]}
        />
      </PageBodyStyled>
      {switchPlanModal}
    </PageTransitionStyled>
  );
};

export default PaicOrgRedesignV2;
